import { useEffect, useRef } from 'react';
import { Box, Typography } from '../../ui';
import { useVideoWatchTracking } from '../../../hooks/useVideoWatchTracking';
import { useVideoSeekGuard } from '../../../hooks/useVideoSeekGuard';
import VideoWatchGuard from './VideoWatchGuard';

interface WistiaPlayerProps {
  mediaId?: string;
  lessonTitle: string;
  posterUrl?: string;
  sx?: any;
  onVideoEnded?: () => void;
  // When true, onVideoEnded fires only after the learner has watched enough of
  // the video (so scrubbing to the end doesn't count). When false, the native
  // 'ended' event marks complete — used for creators/admins who bypass.
  requireFullWatch?: boolean;
}

/**
 * Renders a Wistia video using the <wistia-player> web component.
 * The player.js script is loaded globally from public/index.html.
 *
 * The web component dispatches standard HTML5 media events (timeupdate/ended),
 * so completion tracking mirrors the Mux player.
 */
export default function WistiaPlayer({ mediaId, lessonTitle, posterUrl, sx, onVideoEnded, requireFullWatch = true }: WistiaPlayerProps) {
  const playerRef = useRef<any>(null);

  useVideoWatchTracking(playerRef, {
    enabled: requireFullWatch,
    resetKey: mediaId,
    onComplete: () => onVideoEnded?.(),
  });

  // Anti-skip guard — learners only (admins keep requireFullWatch=false).
  const seekGuard = useVideoSeekGuard(playerRef, {
    enabled: requireFullWatch,
    resetKey: mediaId,
  });

  // Creators/admins bypass the full-watch requirement: simple 'ended' listener.
  useEffect(() => {
    if (requireFullWatch) return;
    const player = playerRef.current;
    if (!player || !onVideoEnded) return;

    const handleEnded = () => onVideoEnded();
    player.addEventListener('ended', handleEnded);
    return () => player.removeEventListener('ended', handleEnded);
  }, [onVideoEnded, mediaId, requireFullWatch]);

  if (!mediaId) {
    return (
      <Box sx={{ p: 3, textAlign: 'center', color: 'text.secondary', ...sx }}>
        <Typography variant="body2">No Wistia video ID configured for this lesson</Typography>
      </Box>
    );
  }

  return (
    <VideoWatchGuard enabled={requireFullWatch} {...seekGuard}>
      <Box
        // Keying on media-id forces a fresh <wistia-player> mount when the
        // learner switches lessons. Same reason as the Mux branch: changing
        // media-id on a live web-component element doesn't reliably reset
        // the underlying video stream.
        key={mediaId}
        ref={playerRef}
        component="wistia-player"
        media-id={mediaId}
        aspect="1.7777777777777777"
        {...(posterUrl ? { 'media-poster': posterUrl } : {})}
        title={lessonTitle}
        sx={{
          display: 'block',
          width: '100%',
          height: '700px',
          ...sx,
        }}
      />
    </VideoWatchGuard>
  );
}
